/**
 * Permanently deleting a member's garments: one at a time, a ticked
 * selection, or all of them.
 *
 * The backend is adding these endpoints, so the generated client may not have
 * them yet. They are looked up by name at runtime, and every control here
 * renders nothing until the client carries the operation. When the client is
 * regenerated the controls appear with no further change.
 */

import { useState } from 'react';
import * as api from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, FailureText } from '@ui/console/primitives';
import { formatCount } from '@ui/console/measures';
import { ActionDialog } from '@ui/console/PageShell';
import { TextAreaField, TextField } from '@ui/console/forms';
import { configureAdminApi, handleUnauthorized } from './auth';
import { Actions } from './components';
import { errorDetail } from './operation-policy';

/**
 * The one place that knows what the generated client calls the two operations.
 * A generated name ends in its path and method, so the suffix identifies
 * `DELETE /api/v1/internal/users/{id}/wardrobe/garments/{garment_id}/permanent`
 * and `POST /api/v1/internal/users/{id}/wardrobe/garments/delete-bulk`
 * whatever the prefix becomes. The older archive-only
 * `…WardrobeGarmentsGarmentIdDelete` route does not match either.
 */
export const GARMENT_PERMANENT_DELETE_OPERATION =
  /ApiV1InternalUsersIdWardrobeGarmentsGarmentIdPermanentDelete$/;
export const GARMENT_BULK_DELETE_OPERATION = /ApiV1InternalUsersIdWardrobeGarmentsDeleteBulkPost$/;

type Reply<T> = { data?: T | undefined; error?: unknown; response: { status: number } };

type DeleteGarmentOperation = (options: {
  path: { id: string; garment_id: string };
  body: { reason: string };
}) => Promise<Reply<unknown>>;

type BulkBody = { reason: string } & ({ garment_ids: string[] } | { all: true });

export type BulkGarmentOutcome = { garment_id: string; ok: boolean; error_code?: string | null };

type BulkReply = {
  results?: BulkGarmentOutcome[];
  deleted?: number;
  remaining?: number;
  [key: string]: unknown;
};

type BulkGarmentOperation = (options: {
  path: { id: string };
  body: BulkBody;
}) => Promise<Reply<BulkReply>>;

function findOperation(pattern: RegExp): unknown {
  return Object.entries(api as Record<string, unknown>).find(
    ([name, value]) => pattern.test(name) && typeof value === 'function',
  )?.[1];
}

export function garmentDeletionOperations() {
  return {
    single: findOperation(GARMENT_PERMANENT_DELETE_OPERATION) as DeleteGarmentOperation | undefined,
    bulk: findOperation(GARMENT_BULK_DELETE_OPERATION) as BulkGarmentOperation | undefined,
  };
}

/** What permanent deletion does, in words for the operator confirming it. */
export const GARMENT_DELETION_CONSEQUENCE =
  'This is permanent and cannot be undone. The garment’s photos are removed. It is taken out of every outfit it is in, and any outfit left with fewer than 2 garments is deleted too.';

const MANY_CONSEQUENCE =
  'This is permanent and cannot be undone. Their photos are removed. They are taken out of every outfit they are in, and any outfit left with fewer than 2 garments is deleted too.';

/** The request's size limit on the bulk route. */
const MAX_IDS_PER_CALL = 500;

/** The per-garment list, whatever the reply calls it. */
function outcomesOf(reply: BulkReply): BulkGarmentOutcome[] {
  if (Array.isArray(reply.results)) return reply.results;
  for (const value of Object.values(reply)) {
    if (
      Array.isArray(value) &&
      value.every((item) => item && typeof item === 'object' && 'garment_id' in item)
    )
      return value as BulkGarmentOutcome[];
  }
  return [];
}

/** A refused request, in words a member of staff can act on. */
function refusal(status: number, error: unknown, many: boolean): string {
  if (status === 403)
    return 'Your role isn’t allowed to permanently delete a member’s garments. Ask an admin if you need this.';
  if (status === 404)
    return many
      ? 'This member, or the garments you picked, could not be found. They may already have been deleted.'
      : 'This garment could not be found in this member’s wardrobe. It may already have been deleted.';
  if (status === 422)
    return `The request was not accepted: ${errorDetail(error, 'something in it was missing or invalid')}. Check the reason and try again.`;
  return errorDetail(
    error,
    many ? 'The garments could not be deleted.' : 'The garment could not be deleted.',
  );
}

/** The per-garment codes the bulk delete answers with. */
const OUTCOME_REASON: Record<string, string> = {
  not_found: 'It was already gone.',
  delete_failed: 'The delete did not go through. Try it again.',
};

function outcomeReason(code: string | null | undefined) {
  if (!code) return 'It could not be deleted.';
  return `${OUTCOME_REASON[code] ?? 'It could not be deleted.'} (${code})`;
}

function ReasonField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <TextAreaField
      label="Reason"
      hint="Required. Recorded with your name in the audit log."
      value={value}
      rows={3}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/* ------------------------------------------------------------ one garment */

export function DeleteGarmentAction({
  memberId,
  garmentId,
  title,
  onDeleted,
}: {
  memberId: string;
  garmentId: string;
  title: string;
  onDeleted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const operation = garmentDeletionOperations().single;
  if (!operation) return null;

  const run = async () => {
    setBusy(true);
    setFailure(undefined);
    try {
      configureAdminApi();
      const result = await operation({
        path: { id: memberId, garment_id: garmentId },
        body: { reason: reason.trim() },
      });
      handleUnauthorized(result.response.status);
      if (result.error || result.response.status >= 300) {
        setFailure(refusal(result.response.status, result.error, false));
        return;
      }
      setOpen(false);
      onDeleted();
    } catch (cause) {
      setFailure(errorDetail(cause, 'The garment could not be deleted. Check your connection.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        color="secondary-destructive"
        onClick={() => {
          setReason('');
          setFailure(undefined);
          setOpen(true);
        }}
      >
        Delete permanently
      </Button>
      {open ? (
        <ActionDialog
          title={`Permanently delete “${title}”?`}
          description={GARMENT_DELETION_CONSEQUENCE}
          onClose={() => (busy ? undefined : setOpen(false))}
        >
          <div className="flex flex-col gap-4">
            {failure ? (
              <Callout tone="error" title="The garment was not deleted.">
                <FailureText raw={failure} />
              </Callout>
            ) : null}
            <ReasonField value={reason} onChange={setReason} />
            <Actions>
              <Button size="sm" color="secondary" isDisabled={busy} onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                color="primary-destructive"
                isDisabled={busy || !reason.trim()}
                onClick={() => void run()}
              >
                {busy ? 'Deleting…' : 'Delete permanently'}
              </Button>
            </Actions>
          </div>
        </ActionDialog>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------ many garments */

type BulkMode = 'selected' | 'all';

type BulkReport = {
  deleted: number;
  failed: BulkGarmentOutcome[];
  /** Garments the server still holds when deleting stopped making progress. */
  leftOver: number;
};

/** A refusal already worded for the operator. */
class Refused extends Error {}

/** Enough calls to clear any plausible wardrobe, and a hard stop if one never drains. */
const MAX_BULK_CALLS = 500;

export function BulkGarmentDeletion({
  memberId,
  memberName,
  selectedIds,
  total,
  titles,
  onClearSelection,
  onDeleted,
}: {
  memberId: string;
  memberName?: string | null | undefined;
  selectedIds: readonly string[];
  /** Every garment on record for the member, not just the ones on screen. 0 hides “delete all”. */
  total: number;
  titles: ReadonlyMap<string, string>;
  onClearSelection: () => void;
  onDeleted: () => void;
}) {
  const [mode, setMode] = useState<BulkMode>();
  // Fixed when the dialog opens, so the list refreshing underneath it does not
  // change the figure the operator confirmed.
  const [count, setCount] = useState(0);
  const [targets, setTargets] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ deleted: number; remaining: number }>();
  const [failure, setFailure] = useState<string>();
  const [report, setReport] = useState<BulkReport>();
  const operation = garmentDeletionOperations().bulk;
  if (!operation) return null;

  const name = memberName?.trim();
  const typedOk =
    mode !== 'all' ||
    typed.trim() === 'DELETE' ||
    (Boolean(name) && typed.trim().toLowerCase() === name!.toLowerCase());
  const ready = Boolean(reason.trim()) && typedOk && count > 0;

  const start = (next: BulkMode) => {
    setMode(next);
    setCount(next === 'all' ? total : selectedIds.length);
    setTargets(next === 'all' ? [] : [...selectedIds]);
    setReason('');
    setTyped('');
    setFailure(undefined);
    setReport(undefined);
    setProgress(undefined);
  };

  const close = () => {
    setMode(undefined);
    setReport(undefined);
  };

  const call = async (body: BulkBody) => {
    configureAdminApi();
    const result = await operation({ path: { id: memberId }, body });
    handleUnauthorized(result.response.status);
    if (result.error || result.response.status >= 300 || !result.data) {
      throw new Refused(refusal(result.response.status, result.error, true));
    }
    return result.data;
  };

  const run = async () => {
    if (!mode) return;
    setBusy(true);
    setFailure(undefined);
    const text = reason.trim();
    let deleted = 0;
    const failed = new Map<string, BulkGarmentOutcome>();
    let leftOver = 0;
    try {
      if (mode === 'all') {
        for (let calls = 0; calls < MAX_BULK_CALLS; calls += 1) {
          const reply = await call({ all: true, reason: text });
          const outcomes = outcomesOf(reply);
          const deletedNow = reply.deleted ?? outcomes.filter((item) => item.ok).length;
          deleted += deletedNow;
          for (const item of outcomes) {
            if (item.ok) failed.delete(item.garment_id);
            else failed.set(item.garment_id, item);
          }
          const remaining = reply.remaining ?? 0;
          setProgress({ deleted, remaining });
          if (remaining <= 0) break;
          // Nothing moved this time, so another call would only repeat itself.
          if (deletedNow === 0) {
            leftOver = Math.max(0, remaining - failed.size);
            break;
          }
        }
      } else {
        let pending = [...targets];
        for (let calls = 0; pending.length && calls < MAX_BULK_CALLS; calls += 1) {
          const batch = pending.slice(0, MAX_IDS_PER_CALL);
          const reply = await call({ garment_ids: batch, reason: text });
          const outcomes = outcomesOf(reply);
          deleted += reply.deleted ?? outcomes.filter((item) => item.ok).length;
          for (const item of outcomes) if (!item.ok) failed.set(item.garment_id, item);
          const answered = new Set(outcomes.map((item) => item.garment_id));
          setProgress({ deleted, remaining: pending.length - answered.size });
          // A batch the server answered nothing about would be sent again forever.
          if (!answered.size) {
            leftOver = pending.length;
            break;
          }
          pending = pending.filter((id) => !answered.has(id));
        }
      }
      setReport({ deleted, failed: [...failed.values()], leftOver });
    } catch (cause) {
      setFailure(
        cause instanceof Refused
          ? cause.message
          : errorDetail(cause, 'The garments could not be deleted. Check your connection.'),
      );
      // Whatever went before the refusal did happen, so the report still counts it.
      if (deleted) setReport({ deleted, failed: [...failed.values()], leftOver });
    } finally {
      setBusy(false);
      if (deleted) {
        onClearSelection();
        onDeleted();
      }
    }
  };

  const noun = (n: number) => (n === 1 ? 'garment' : 'garments');

  return (
    <>
      <Actions>
        {selectedIds.length ? (
          <Button size="sm" color="secondary-destructive" onClick={() => start('selected')}>
            {`Delete selected (${formatCount(selectedIds.length)})`}
          </Button>
        ) : null}
        {total > 0 ? (
          <Button size="sm" color="secondary-destructive" onClick={() => start('all')}>
            Delete all garments
          </Button>
        ) : null}
      </Actions>
      {mode ? (
        <ActionDialog
          title={
            mode === 'all'
              ? `Permanently delete all ${formatCount(count)} ${noun(count)}?`
              : `Permanently delete ${formatCount(count)} selected ${noun(count)}?`
          }
          description={
            mode === 'all'
              ? `Every garment this member has — ${formatCount(count)} in total, archived ones and ones not shown on this page included — is deleted. ${MANY_CONSEQUENCE}`
              : `The ticked garments are deleted. ${MANY_CONSEQUENCE}`
          }
          width="lg"
          onClose={() => (busy ? undefined : close())}
        >
          <div className="flex flex-col gap-4">
            {failure ? (
              <Callout tone="error" title="Deleting stopped.">
                <FailureText raw={failure} />
              </Callout>
            ) : null}
            {busy && progress ? (
              <p className="text-sm text-secondary" aria-live="polite">
                {`Deleted ${formatCount(progress.deleted)} so far${progress.remaining ? `, ${formatCount(progress.remaining)} still to go` : ''}…`}
              </p>
            ) : busy ? (
              <p className="text-sm text-secondary" aria-live="polite">
                Deleting…
              </p>
            ) : null}
            {report ? (
              <Callout
                tone={report.failed.length || report.leftOver ? 'warning' : 'success'}
                title={`${formatCount(report.deleted)} ${noun(report.deleted)} deleted.`}
              >
                {report.failed.length ? (
                  <div className="flex flex-col gap-2">
                    <span>
                      {`${formatCount(report.failed.length)} ${noun(report.failed.length)} could not be deleted and ${report.failed.length === 1 ? 'is' : 'are'} still there:`}
                    </span>
                    <ul className="flex flex-col gap-1">
                      {report.failed.map((item) => (
                        <li key={item.garment_id} className="text-sm">
                          <span className="font-medium">
                            {titles.get(item.garment_id) ?? 'A garment not on this page'}
                          </span>
                          {' — '}
                          {outcomeReason(item.error_code)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {report.leftOver ? (
                  <span className="block">
                    {`${formatCount(report.leftOver)} ${noun(report.leftOver)} ${report.leftOver === 1 ? 'was' : 'were'} left in place because deleting stopped making progress. Try again, or ask an engineer to look.`}
                  </span>
                ) : null}
                {!report.failed.length && !report.leftOver ? (
                  <span>Every garment you asked for was deleted.</span>
                ) : null}
              </Callout>
            ) : null}
            {report ? (
              <Actions>
                <Button size="sm" color="primary" onClick={close}>
                  Done
                </Button>
              </Actions>
            ) : (
              <>
                <ReasonField value={reason} onChange={setReason} />
                {mode === 'all' ? (
                  <TextField
                    label={
                      name
                        ? `Type the member’s name (${name}) or DELETE to confirm`
                        : 'Type DELETE to confirm'
                    }
                    value={typed}
                    onChange={(event) => setTyped(event.target.value)}
                  />
                ) : null}
                <Actions>
                  <Button size="sm" color="secondary" isDisabled={busy} onClick={close}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    color="primary-destructive"
                    isDisabled={busy || !ready}
                    onClick={() => void run()}
                  >
                    {busy
                      ? 'Deleting…'
                      : mode === 'all'
                        ? `Delete all ${formatCount(count)} ${noun(count)}`
                        : `Delete ${formatCount(count)} ${noun(count)}`}
                  </Button>
                </Actions>
              </>
            )}
          </div>
        </ActionDialog>
      ) : null}
    </>
  );
}
